import { useMemo, useState } from 'react'
import {
  Box, Breadcrumbs, Typography, Stack, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Alert,
} from '@mui/material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  listAllExpenseReports, scheduleExpenseReportPayment, payExpenseReport,
  type ExpenseReportListItem, type ExpenseReportStatus,
} from '../../api/deconturi'
import { listCashAccounts } from '../../api/financiar'
import { errorMessage } from '../../api/client'

const STATUS_LABEL: Record<ExpenseReportStatus, string> = {
  Draft: 'Ciornă', Depusa: 'În aprobare', Aprobat: 'Aprobat', Respins: 'Respins', Programat: 'Programat la plată', Decontat: 'Decontat',
}
const STATUS_COLOR: Record<ExpenseReportStatus, 'default' | 'success' | 'error' | 'warning' | 'info'> = {
  Draft: 'default', Depusa: 'info', Aprobat: 'success', Respins: 'error', Programat: 'warning', Decontat: 'success',
}

export default function DeconturiToatePage() {
  const [scheduleTarget, setScheduleTarget] = useState<ExpenseReportListItem | null>(null)
  const [payTarget, setPayTarget] = useState<ExpenseReportListItem | null>(null)
  const { data, isLoading } = useQuery({ queryKey: ['cheltuieli', 'deconturi-toate'], queryFn: listAllExpenseReports })

  const columns = useMemo<GridColDef<ExpenseReportListItem>[]>(() => [
    { field: 'number', headerName: 'Nr.', width: 150, renderCell: (p) => <code>{p.row.number}</code> },
    { field: 'employeeName', headerName: 'Angajat', flex: 1, minWidth: 180 },
    { field: 'total', headerName: 'Total', width: 110, type: 'number', valueFormatter: (v: number) => v.toFixed(2).replace('.', ',') },
    {
      field: 'status', headerName: 'Status', width: 150, sortable: false,
      renderCell: (p) => <Chip size="small" label={STATUS_LABEL[p.row.status]} color={STATUS_COLOR[p.row.status]} variant="outlined" />,
    },
    { field: 'scheduledPaymentDate', headerName: 'Programat la', width: 130, renderCell: (p) => p.row.scheduledPaymentDate ?? '—' },
    {
      field: 'actions', headerName: '', width: 220, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <Stack direction="row" spacing={1}>
          {p.row.status === 'Aprobat' && (
            <Button size="small" onClick={() => setScheduleTarget(p.row)}>Programează plata</Button>
          )}
          {p.row.status === 'Programat' && (
            <Button size="small" variant="contained" onClick={() => setPayTarget(p.row)}>Plătește</Button>
          )}
        </Stack>
      ),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Cheltuieli</Typography>
        <Typography variant="inherit" color="text.primary">Deconturi (toate)</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Deconturi — toți angajații</Typography>
      <Box sx={{ height: 600, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>
      <SchedulePaymentDialog target={scheduleTarget} onClose={() => setScheduleTarget(null)} />
      <PayDialog target={payTarget} onClose={() => setPayTarget(null)} />
    </Stack>
  )
}

function SchedulePaymentDialog({ target, onClose }: { target: ExpenseReportListItem | null; onClose: () => void }) {
  const open = target !== null
  const qc = useQueryClient()
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))

  const mutation = useMutation({
    mutationFn: () => scheduleExpenseReportPayment(target!.id, date),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['cheltuieli', 'deconturi-toate'] }); onClose() },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Programează plata — {target?.number}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField type="date" label="Dată programată" value={date} onChange={(e) => setDate(e.target.value)} fullWidth slotProps={{ inputLabel: { shrink: true } }} />
          {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Renunță</Button>
        <Button variant="contained" disabled={mutation.isPending} onClick={() => mutation.mutate()}>Programează</Button>
      </DialogActions>
    </Dialog>
  )
}

function PayDialog({ target, onClose }: { target: ExpenseReportListItem | null; onClose: () => void }) {
  const open = target !== null
  const qc = useQueryClient()
  const { data: accounts } = useQuery({ queryKey: ['financiar', 'conturi'], queryFn: listCashAccounts, enabled: open })
  const [cashAccountId, setCashAccountId] = useState('')
  const [currency, setCurrency] = useState('RON')
  const [exchangeRate, setExchangeRate] = useState('1')

  const mutation = useMutation({
    mutationFn: () => payExpenseReport(target!.id, cashAccountId, currency, Number(exchangeRate)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cheltuieli', 'deconturi-toate'] })
      setCashAccountId('')
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Plătește decontul — {target?.number}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField select label="Cont" value={cashAccountId} onChange={(e) => setCashAccountId(e.target.value)} fullWidth>
            {accounts?.filter((a) => a.isActive).map((a) => <MenuItem key={a.id} value={a.id}>{a.name} ({a.currency})</MenuItem>)}
          </TextField>
          <Stack direction="row" spacing={2}>
            <TextField label="Monedă" value={currency} onChange={(e) => setCurrency(e.target.value)} sx={{ width: 110 }} />
            {currency !== 'RON' && <TextField type="number" label="Curs/RON" value={exchangeRate} onChange={(e) => setExchangeRate(e.target.value)} fullWidth />}
          </Stack>
          {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Renunță</Button>
        <Button variant="contained" disabled={!cashAccountId || mutation.isPending} onClick={() => mutation.mutate()}>Plătește</Button>
      </DialogActions>
    </Dialog>
  )
}
